import { useAppDispatch, useAppSelector } from '@hooks/useRedux';
import { useUpdateProperty } from '@hooks/accommodation';
import {
  createPropertyThunk,
  fetchPropertyThunk,
  selectAccommodationProperty,
} from '@store/accommodation';
import { selectEffectiveOutletId } from '@store/outlets';
import { selectUserCurrencyPrefix } from '@store/auth/authSelectors';
import { Building2, Edit2, ImageIcon, Plus, X } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { TIMEZONES } from 'utils/constants';
import { FormInput, FormSelect, FormTextarea } from '../../../common/FormField';
import { apiError, apiSuccess } from '../../../../util/notify';
import { uploadPropertyImage } from '../../../../services/accommodationService';

interface PropertyForm {
  name: string;
  address: string;
  timezone: string;
  currency: string;
  check_in_time: string;
  check_out_time: string;
  cancellation_policy: string;
}

const emptyForm = (currencyPrefix: string): PropertyForm => ({
  name: '',
  address: '',
  timezone: 'UTC',
  currency: currencyPrefix,
  check_in_time: '14:00',
  check_out_time: '11:00',
  cancellation_policy: '',
});

function PropertyFormModal({
  title,
  initial,
  existingImageUrl,
  onSubmit,
  onClose,
  isSubmitting,
}: {
  title: string;
  initial: PropertyForm;
  existingImageUrl?: string | null;
  onSubmit: (
    form: PropertyForm,
    pendingFile: File | null,
    removeImage: boolean,
  ) => void;
  onClose: () => void;
  isSubmitting: boolean;
}) {
  const [form, setForm] = useState<PropertyForm>(initial);
  const set = (key: keyof PropertyForm, val: string) =>
    setForm((f) => ({ ...f, [key]: val }));
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [removeImage, setRemoveImage] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const displayUrl =
    previewUrl ?? (removeImage ? null : (existingImageUrl ?? null));

  const handleFile = (file: File) => {
    const allowed = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowed.includes(file.type)) return;
    setPendingFile(file);
    setPreviewUrl(URL.createObjectURL(file));
    setRemoveImage(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
      <div className="w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-6 shadow-xl">
        <div className="mb-5 flex items-center justify-between">
          <h4 className="font-semibold text-gray-900">{title}</h4>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100"
          >
            <X size={16} />
          </button>
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            onSubmit(form, pendingFile, removeImage);
          }}
          className="space-y-4"
        >
          {/* Property image */}
          <div>
            <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
              Property photo
            </p>
            {displayUrl ? (
              <div>
                <div className="relative inline-block">
                  <img
                    src={displayUrl}
                    alt="Property"
                    className="h-28 w-44 rounded-xl border border-gray-200 object-cover"
                  />
                  <button
                    type="button"
                    aria-label="Remove photo"
                    onClick={() => {
                      setPendingFile(null);
                      setPreviewUrl(null);
                      setRemoveImage(true);
                    }}
                    className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-500 shadow hover:text-red-500"
                  >
                    <X size={12} />
                  </button>
                </div>
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="mt-1.5 flex items-center gap-1 text-xs text-indigo-600 hover:underline"
                >
                  Change photo
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragOver(true);
                }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setDragOver(false);
                  const file = e.dataTransfer.files[0];
                  if (file) handleFile(file);
                }}
                className={`flex h-24 w-full cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed transition ${dragOver ? 'border-indigo-400 bg-indigo-50' : 'border-gray-200 bg-gray-50 hover:border-gray-300'}`}
              >
                <ImageIcon size={22} className="text-gray-400" />
                <span className="text-xs text-gray-500">
                  Drag &amp; drop or{' '}
                  <span className="font-medium text-indigo-600">browse</span>
                </span>
                <span className="text-[10px] text-gray-400">
                  JPG, PNG, WebP — resized to max 1 MB
                </span>
              </button>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleFile(file);
                e.target.value = '';
              }}
            />
          </div>

          <FormInput
            label="Property name"
            required
            value={form.name}
            onChange={(e) => set('name', e.target.value)}
            placeholder="e.g. Sunset Hotel"
          />
          <FormInput
            label="Address"
            value={form.address}
            onChange={(e) => set('address', e.target.value)}
            placeholder="Physical address"
          />
          <div className="grid grid-cols-2 gap-4">
            <FormSelect
              label="Timezone"
              value={form.timezone}
              onChange={(e) => set('timezone', e.target.value)}
            >
              {TIMEZONES.map((tz) => (
                <option key={tz} value={tz}>
                  {tz}
                </option>
              ))}
            </FormSelect>
            <FormInput
              label="Currency"
              value={form.currency}
              onChange={(e) => set('currency', e.target.value.toUpperCase())}
              maxLength={3}
              placeholder={initial.currency}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <FormInput
              label="Default check-in"
              type="time"
              value={form.check_in_time}
              onChange={(e) => set('check_in_time', e.target.value)}
            />
            <FormInput
              label="Default check-out"
              type="time"
              value={form.check_out_time}
              onChange={(e) => set('check_out_time', e.target.value)}
            />
          </div>
          <FormTextarea
            label="Cancellation policy"
            rows={3}
            value={form.cancellation_policy}
            onChange={(e) => set('cancellation_policy', e.target.value)}
            placeholder="e.g. Full refund if cancelled 48 hours before check-in."
          />

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="rounded-xl bg-pos-primary px-5 py-2 text-sm font-medium text-white hover:bg-pos-primary/90 disabled:opacity-60"
            >
              {isSubmitting ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export const PropertiesPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const property = useAppSelector(selectAccommodationProperty);
  const updateProperty = useUpdateProperty();
  const isSubmitting = updateProperty.isPending;
  const outletId = useAppSelector(selectEffectiveOutletId);
  const userCurrencyPrefix = useAppSelector(selectUserCurrencyPrefix);

  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(false);

  useEffect(() => {
    if (outletId && !property) {
      dispatch(fetchPropertyThunk(outletId));
    }
  }, [dispatch, outletId, property]);

  const handleCreate = async (form: PropertyForm, pendingFile: File | null) => {
    if (!outletId) return;
    try {
      const created = await dispatch(
        createPropertyThunk({ outletId, name: form.name }),
      ).unwrap();
      if (pendingFile && created?.id) {
        try {
          const imageUrl = await uploadPropertyImage(created.id, pendingFile);
          updateProperty.mutate({
            id: created.id,
            updates: { image_url: imageUrl },
          });
        } catch {
          // Non-fatal: property created without image
        }
      }
      apiSuccess('Property created.');
      setShowCreate(false);
    } catch (err) {
      // unwrap() rejects with the thunk's rejectWithValue string
      apiError(typeof err === 'string' ? err : 'Failed to create property');
    }
  };

  const handleEdit = async (
    form: PropertyForm,
    pendingFile: File | null,
    removeImage: boolean,
  ) => {
    if (!property) return;
    let imageUrl: string | null | undefined;
    if (pendingFile) {
      try {
        imageUrl = await uploadPropertyImage(property.id, pendingFile);
      } catch {
        apiError('Image upload failed. Please try again.');
        return;
      }
    } else if (removeImage) {
      imageUrl = null;
    }
    updateProperty.mutate(
      {
        id: property.id,
        updates: {
          name: form.name,
          address: form.address || null,
          timezone: form.timezone,
          currency: form.currency,
          check_in_time: form.check_in_time,
          check_out_time: form.check_out_time,
          cancellation_policy: form.cancellation_policy || null,
          ...(imageUrl !== undefined && { image_url: imageUrl }),
        },
      },
      {
        onSuccess: () => {
          apiSuccess('Property updated.');
          setShowEdit(false);
        },
      },
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">Properties</h3>
          <p className="text-xs text-gray-400">
            Manage your accommodation properties
          </p>
        </div>
        {!property && (
          <button
            type="button"
            onClick={() => setShowCreate(true)}
            className="flex items-center gap-1.5 rounded-xl bg-pos-primary px-3 py-2 text-xs font-medium text-white hover:bg-pos-primary/90"
          >
            <Plus size={13} />
            Create property
          </button>
        )}
      </div>

      {!property ? (
        <div className="flex h-48 flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-gray-200">
          <Building2 size={28} className="text-gray-300" />
          <div className="text-center">
            <p className="text-sm font-medium text-gray-600">
              No property set up
            </p>
            <p className="text-xs text-gray-400">
              Create a property to start managing bookings
            </p>
          </div>
          <button
            type="button"
            onClick={() => setShowCreate(true)}
            className="flex items-center gap-1.5 rounded-xl bg-pos-primary px-4 py-2 text-sm font-medium text-white hover:bg-pos-primary/90"
          >
            <Plus size={14} />
            Create your first property
          </button>
        </div>
      ) : (
        <div className="rounded-2xl border border-gray-200 bg-white">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 text-left">
                <th className="px-5 py-3 text-xs font-semibold text-gray-500">
                  Name
                </th>
                <th className="px-5 py-3 text-xs font-semibold text-gray-500">
                  Address
                </th>
                <th className="px-5 py-3 text-xs font-semibold text-gray-500">
                  Currency
                </th>
                <th className="px-5 py-3 text-xs font-semibold text-gray-500">
                  Timezone
                </th>
                <th className="px-5 py-3 text-xs font-semibold text-gray-500">
                  Check-in / out
                </th>
                <th className="px-5 py-3" aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="px-5 py-3 font-medium text-gray-900">
                  <div className="flex items-center gap-2">
                    {property.image_url && (
                      <img
                        src={property.image_url}
                        alt={property.name}
                        className="h-8 w-12 rounded object-cover"
                      />
                    )}
                    {property.name}
                  </div>
                </td>
                <td className="px-5 py-3 text-gray-600">
                  {property.address ?? '—'}
                </td>
                <td className="px-5 py-3 text-gray-600">{property.currency}</td>
                <td className="px-5 py-3 text-gray-600">{property.timezone}</td>
                <td className="px-5 py-3 text-gray-600">
                  {property.check_in_time} / {property.check_out_time}
                </td>
                <td className="px-5 py-3 text-right">
                  <button
                    type="button"
                    onClick={() => setShowEdit(true)}
                    className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                  >
                    <Edit2 size={14} />
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {showCreate && (
        <PropertyFormModal
          title="Create property"
          initial={emptyForm(userCurrencyPrefix)}
          onSubmit={handleCreate}
          onClose={() => setShowCreate(false)}
          isSubmitting={isSubmitting}
        />
      )}

      {showEdit && property && (
        <PropertyFormModal
          title="Edit property"
          initial={{
            name: property.name,
            address: property.address ?? '',
            timezone: property.timezone,
            currency: property.currency,
            check_in_time: property.check_in_time,
            check_out_time: property.check_out_time,
            cancellation_policy: property.cancellation_policy ?? '',
          }}
          existingImageUrl={property.image_url}
          onSubmit={handleEdit}
          onClose={() => setShowEdit(false)}
          isSubmitting={isSubmitting}
        />
      )}
    </div>
  );
};
